import { useMemo } from 'react'
import { useAuthStore } from '../auth/useAuthStore'
import type { AccountSummary } from '../api/types'

const TYPE_LABEL: Record<string, string> = {
  BROKERAGE: 'Brokerage',
  HIGH_YIELD: 'High-Yield',
  BOND_ACCOUNT: 'Bonds',
  RIA_ASSET: 'RIA',
  TREASURY: 'Treasury',
  TRADITIONAL_IRA: 'Trad IRA',
  ROTH_IRA: 'Roth IRA',
}

export interface AccountInfo {
  label: string
  trailing: string | null
  platformName: string | null
}

function shortenAccountId(value: string): string {
  if (value.length <= 6) return value
  return `…${value.slice(-4)}`
}

export function makeAccountInfo(
  accountId: string | null | undefined,
  accounts: AccountSummary[],
  platformNameById: Map<string, string>,
): AccountInfo {
  if (!accountId) return { label: 'Unknown', trailing: null, platformName: null }
  const a = accounts.find((x) => x.accountId === accountId)
  if (!a) {
    return { label: shortenAccountId(accountId), trailing: null, platformName: null }
  }
  const baseLabel = a.displayName ?? TYPE_LABEL[a.accountType] ?? a.accountType
  const margin =
    a.accountType === 'BROKERAGE' && a.brokerageAccountType
      ? a.brokerageAccountType.toLowerCase()
      : null
  const trailing = a.accountNumber ? shortenAccountId(a.accountNumber) : null
  const platformName = platformNameById.get(a.platformId) ?? null
  return {
    label: margin ? `${baseLabel} · ${margin}` : baseLabel,
    trailing,
    platformName,
  }
}

export function useAccountLabeler(): (accountId: string | null | undefined) => AccountInfo {
  const accounts = useAuthStore((s) => s.accounts)
  const platforms = useAuthStore((s) => s.platforms)
  const platformNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of platforms) m.set(p.platformId, p.displayName)
    return m
  }, [platforms])
  return (id) => makeAccountInfo(id, accounts, platformNameById)
}

interface AccountChipProps {
  accountId: string | null | undefined
  size?: 'sm' | 'xs'
  withTrailing?: boolean
  withPlatform?: boolean
  title?: string
}

export function AccountChip({
  accountId,
  size = 'xs',
  withTrailing = true,
  withPlatform = false,
  title,
}: AccountChipProps) {
  const label = useAccountLabeler()
  const info = label(accountId)
  const padding = size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-1.5 py-0.5 text-[10px]'
  return (
    <span
      title={title ?? `${info.label}${info.trailing ? ` · ${info.trailing}` : ''}${info.platformName ? ` · ${info.platformName}` : ''}`}
      className={`inline-flex items-center gap-1 rounded border bg-emerald-950/30 text-emerald-300 border-emerald-900/60 font-medium uppercase tracking-wide ${padding}`}
    >
      <span className="w-1 h-1 rounded-full bg-emerald-400" />
      <span className="normal-case tracking-normal">{info.label}</span>
      {withTrailing && info.trailing && (
        <span className="font-mono normal-case tracking-normal text-emerald-400/70">
          {info.trailing}
        </span>
      )}
      {withPlatform && info.platformName && (
        <span className="normal-case tracking-normal text-emerald-400/70">· {info.platformName}</span>
      )}
    </span>
  )
}

export function useIsAggregateScope(): boolean {
  const scopeAccountId = useAuthStore((s) => s.scopeAccountId)
  const accounts = useAuthStore((s) => s.accounts)
  return scopeAccountId === null && accounts.length > 1
}
